import { boxOf, cellAtPoint, findIt, fits, resizeRect, span, zRange } from '../grid/engine.js';
import type { Cell, Corner } from '../grid/engine.js';
import type { WidgetType } from '../contract.js';
import { editDoc } from './derive.js';
import { docNext } from './doc.js';
import { panBy, removeW, resizeBy, stepW, zoomW } from './edit.js';
import type { AppStore } from './store.js';
import type { Patch } from './types.js';

/**
 * Pointer and keyboard gestures on the widgets of an editable grid, ported from `wDown`, `wMove`,
 * `wUp`, `wKey` and `wheel` (04 §4–§7). One gesture at a time, kept outside the app state (`_pd`).
 */

type Mode = 'move' | 'resize' | 'pan' | 'pinch' | 'tap';
interface Gesture {
  id: string; mode: Mode; corner: Corner | ''; pid: number; sx: number; sy: number; x1: number; y1: number; sc: number;
  c0: number; r0: number; w0: number; h0: number; px0: number; py0: number; z0: number; ox: number; oy: number; t: WidgetType;
  moved: boolean; last: string; p2?: number; x2?: number; y2?: number; dist0?: number;
}
const gestures = new WeakMap<AppStore, Gesture>();

/** Forget the gesture in progress (leaving the editor, saving, opening a prompt). */
export const resetPointer = (s: AppStore): void => { gestures.delete(s); };

export interface PointerLike {
  readonly pointerType?: string; readonly button?: number; readonly pointerId: number; readonly clientX: number; readonly clientY: number;
  readonly currentTarget: { setPointerCapture?: (id: number) => void } | null;
  stopPropagation?: () => void;
}

/**
 * A pointer goes down on a widget part: `move` (the grip), `resize` (a corner) or `centre`
 * (text: a tap writes; song: moves; photo and video: reframes, and a second finger pinches).
 * `scale` is the stage's current scale, so distances are in stage pixels.
 */
export function wDown(s: AppStore, id: string, part: 'move' | 'resize' | 'centre', corner: Corner | '', e: PointerLike, scale: number): void {
  const st = s.getState(), doc = editDoc(st);
  if (!doc || doc.saving) return;
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  const it = findIt(doc.items, id);
  if (!it) return;
  e.stopPropagation?.();
  const capture = () => { try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch { /* the pointer is already gone */ } };
  const pd = gestures.get(s);
  if (pd && pd.id === id && (pd.mode === 'pan' || pd.mode === 'pinch') && e.pointerId !== pd.pid) {
    pd.mode = 'pinch'; pd.p2 = e.pointerId; pd.x2 = e.clientX; pd.y2 = e.clientY;
    pd.dist0 = Math.max(10, Math.hypot(pd.x2 - pd.x1, pd.y2 - pd.y1)); pd.z0 = it.z || 1;
    capture();
    return;
  }
  const mode: Mode = part === 'centre' ? (it.t === 'text' ? 'tap' : it.t === 'song' ? 'move' : 'pan') : part;
  const px0 = it.px ?? 0.5, py0 = it.py ?? 0.5;
  const b = it.t === 'photo' || it.t === 'video' ? boxOf(it, span(it.w), span(it.h), px0, py0, it.z || 1) : { ox: 0, oy: 0 };
  gestures.set(s, {
    id, mode, corner, pid: e.pointerId, sx: e.clientX, sy: e.clientY, x1: e.clientX, y1: e.clientY, sc: scale || 1,
    c0: it.c, r0: it.r, w0: it.w, h0: it.h, px0, py0, z0: it.z || 1, ox: b.ox || 0, oy: b.oy || 0, t: it.t, moved: false, last: '',
  });
  capture();
  const patch: Patch = {};
  if (st.sel !== id) { patch.sel = id; patch.swOpen = false; }
  if (st.tray) patch.tray = null;
  if (st.txt && st.txt !== id) patch.txt = null;
  if (Object.keys(patch).length) s.set(patch);
}

export function wMove(s: AppStore, e: { readonly pointerId?: number; readonly clientX: number; readonly clientY: number }): void {
  const d = gestures.get(s);
  if (!d) return;
  if (d.mode === 'pinch') {
    if (e.pointerId === d.pid) { d.x1 = e.clientX; d.y1 = e.clientY; } else if (e.pointerId === d.p2) { d.x2 = e.clientX; d.y2 = e.clientY; } else return;
    const [min, max] = zRange(), dist = Math.hypot((d.x2 ?? d.x1) - d.x1, (d.y2 ?? d.y1) - d.y1);
    const z = Math.max(min, Math.min(max, d.z0 * dist / (d.dist0 || 10)));
    s.set({ pdrag: { mode: 'pan', id: d.id, px: d.px0, py: d.py0, z: Math.round(z * 1000) / 1000 } });
    return;
  }
  if (e.pointerId != null && e.pointerId !== d.pid) return;
  const dx = (e.clientX - d.sx) / d.sc, dy = (e.clientY - d.sy) / d.sc;
  d.x1 = e.clientX; d.y1 = e.clientY;
  if (!d.moved && Math.abs(dx) + Math.abs(dy) < 4) return;
  d.moved = true;
  if (d.mode === 'tap') return;
  const doc = editDoc(s.getState());
  if (!doc) return;
  if (d.mode === 'move') {
    const c = Math.max(0, Math.min(7 - d.w0, Math.round(d.c0 + dx / 82))), r = Math.max(0, Math.min(6 - d.h0, Math.round(d.r0 + dy / 82)));
    s.set({ pdrag: { mode: 'move', id: d.id, dx: Math.round(dx), dy: Math.round(dy), c, r, w: d.w0, h: d.h0, ok: fits(doc.items, d.id, c, r, d.w0, d.h0) } });
    return;
  }
  if (d.mode === 'resize') {
    if (!d.corner) return;
    const R = resizeRect({ corner: d.corner, t: d.t, c0: d.c0, r0: d.r0, w0: d.w0, h0: d.h0 }, dx, dy), key = `${R.c},${R.r},${R.w},${R.h}`;
    if (key === d.last) return;
    d.last = key;
    s.set({ pdrag: { mode: 'resize', id: d.id, ok: fits(doc.items, d.id, R.c, R.r, R.w, R.h), ...R } });
    return;
  }
  let px = d.px0, py = d.py0;
  if (d.ox > 0.5) px = Math.max(0, Math.min(1, d.px0 - dx / d.ox));
  if (d.oy > 0.5) py = Math.max(0, Math.min(1, d.py0 - dy / d.oy));
  s.set({ pdrag: { mode: 'pan', id: d.id, px, py } });
}

/** The pointer is released: a tap starts writing; a valid move, resize or reframe is kept; a refused one springs back. */
export function wUp(s: AppStore): void {
  const d = gestures.get(s), st = s.getState(), g = st.pdrag;
  gestures.delete(s);
  if (!d) return;
  if (d.mode === 'tap') { if (!d.moved) s.set({ txt: d.id, sel: d.id, swOpen: false }); return; }
  if (!g || g.id !== d.id) return;
  let patch: Patch | null = null;
  if (g.mode === 'pan') patch = docNext(st, (doc) => { for (const x of doc.items) if (x.id === g.id) { x.px = g.px; x.py = g.py; if (g.z != null) x.z = g.z; } });
  else if (g.ok && (g.c !== d.c0 || g.r !== d.r0 || g.w !== d.w0 || g.h !== d.h0)) patch = docNext(st, (doc) => { for (const x of doc.items) if (x.id === g.id) { x.c = g.c; x.r = g.r; x.w = g.w; x.h = g.h; } });
  s.set({ ...(patch ?? {}), pdrag: null });
}

export interface KeyLike { readonly key: string; readonly altKey: boolean; readonly shiftKey: boolean; preventDefault: () => void }
const ARROWS: Readonly<Record<string, readonly [number, number]>> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

/** Keys on a focused widget (04 §6). */
export function wKey(s: AppStore, id: string, e: KeyLike): void {
  const doc = editDoc(s.getState());
  if (!doc) return;
  const it = findIt(doc.items, id);
  if (!it) return;
  const framed = it.t === 'photo' || it.t === 'video', dir = ARROWS[e.key];
  if (dir) {
    e.preventDefault();
    if (e.altKey) { if (framed) panBy(s, id, dir[0] * 0.1, dir[1] * 0.1); }
    else if (e.shiftKey) resizeBy(s, id, dir[0], dir[1]);
    else stepW(s, id, dir[0], dir[1]);
    return;
  }
  if (e.key === '+' || e.key === '=') { e.preventDefault(); if (framed) zoomW(s, id, 1.2); }
  else if (e.key === '-' || e.key === '_') { e.preventDefault(); if (framed) zoomW(s, id, 1 / 1.2); }
  else if (e.key === 'Enter' && it.t === 'text') { e.preventDefault(); s.set({ txt: id, sel: id, swOpen: false }); }
  else if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); removeW(s, id); }
  else if (e.key === 'Escape') s.set({ sel: null, txt: null, swOpen: false });
}

/** Wheel or two-finger swipe over a photo or video zooms it; with Ctrl (trackpad pinch) three times faster. Returns whether it was used. */
export function wheelZoom(s: AppStore, id: string | null, e: { readonly deltaY: number; readonly deltaMode: number; readonly ctrlKey: boolean }): boolean {
  const st = s.getState(), doc = editDoc(st);
  if (!doc || doc.saving || !id) return false;
  const it = findIt(doc.items, id);
  if (!it || (it.t !== 'photo' && it.t !== 'video')) return false;
  const dy = (e.deltaY || 0) * (e.deltaMode === 1 ? 16 : 1);
  zoomW(s, id, Math.exp(-dy * (e.ctrlKey ? 0.012 : 0.004)));
  if (st.sel !== id || st.swOpen || st.txt) s.set({ sel: id, swOpen: false, txt: null, tray: null });
  return true;
}

/** A press on the empty grid: nothing is selected any more. */
export function backgroundDown(s: AppStore): void {
  const st = s.getState();
  if (st.sel || st.txt || st.tray || st.swOpen) s.set({ sel: null, txt: null, tray: null, swOpen: false });
}

/** The cell under dragged files. `x`, `y` are relative to the grid box as rendered. */
export function dragOverCell(s: AppStore, x: number, y: number, scale: number): Cell | null {
  if (!editDoc(s.getState())) return null;
  const cell = cellAtPoint(x, y, scale), current = s.getState().dropCell;
  if (!current || current.c !== cell.c || current.r !== cell.r) s.set({ dropCell: cell });
  return cell;
}
export function dragLeave(s: AppStore): void {
  if (s.getState().dropCell) s.set({ dropCell: null });
}
